import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagin, { PaginationType } from 'src/components/common/Pagin';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import BtnGradient from '../common/button/BtnGradient';
import BtnOutlineGradient from '../common/button/BtnOutlineGradient';
import { getDetailPagin } from 'src/common/helper';
import { useNotify } from 'src/context/ContextNotify';

const inputStyle = {
  fontSize: '14px',
  height: '32px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
  '& .MuiInputBase-input': { padding: '5px 14px' },
  '& input::placeholder': {
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '130px',
};

const inputMultiStyle = {
  color: '#000',
  width: '250px',
  '&.MuiAutocomplete-root .MuiFormControl-root': {
    backgroundColor: '#FFF',
  },
  '&.MuiAutocomplete-root .MuiOutlinedInput-root': {
    py: '6px',
  },
  '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
    p: 0,
    fontSize: '14px',
  },
};

const PatientList = () => {
  const notifyContext = useNotify();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [tags, setTags] = useState([]);
  const [checkAPI, setCheckAPI] = useState(false);
  const [pagination, setPagination] = useState<PaginationType>();
  const [searchs, setSearchs] = useState({
    patientno: '',
    name: '',
    tel: '',
    email: '',
    quote_id: '',
    tag_ids: [],
    surgeryids: [],
  });

  const getPatientList = (page: number) => {
    setCheckAPI(false);
    const token = getToken();
    postAPI(`/master/patient-search?token=${token}`, {
      page: page,
      sortBy: 'patientno',
      vip: 0,
      ...searchs,
    })
      .then((res) => {
        if (res.code === 200) {
          setPatients(res.data.items);
          setPagination(res.data.pagination);
        }
      })
      .finally(() => {
        setCheckAPI(true);
      });
  };

  const getTags = () => {
    const token = getToken();
    getAPI(`/master/patient-tag-search?token=${token}`)
      .then((res) => {
        if (res.code === 200) {
          setTags(res.data);
        }
      })
      .finally(() => {});
  };

  const getSurgeries = () => {
    const token = getToken();
    postAPI(`/master/surgery-search?token=${token}`, {
      category: '',
      surgery: null,
      status: 1,
      page: 1,
      pagesize: 1000,
    })
      .then((res) => {
        if (res.code === 200) {
          setSurgeries(
            res.data.items.map((item: any) => {
              return { id: item.id, surgery: item.surgery };
            })
          );
        }
      })
      .finally(() => {});
  };

  useEffect(() => {
    getTags();
    getSurgeries();
    getPatientList(1);
    // eslint-disable-next-line
  }, []);

  const handlePageChange = (eventL: any, value: number) => {
    getPatientList(value);
  };

  const handleDetailClick = (patientid: string, privacy: number) => {
    if (privacy === 1) {
      notifyContext.setNotifyMessage({
        show: true,
        message: '表示する権限がありません。',
        type: 'error',
      });
      return false;
    }
    navigate(`/patient/view?id=${patientid}`);
  };
  const handleSearch = () => {
    getPatientList(1);
  };

  const handleCreateNew = () => {
    navigate(`/master/patient-edit`);
  };

  const handleInputSearch = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setSearchs({
      ...searchs,
      [name]: value,
    });
  };

  const handleMultiChange = (event: any, value: any, name: string) => {
    const status = value.map((val: any) => Number(val.id));
    setSearchs({
      ...searchs,
      [name]: status,
    });
  };

  return (
    <Stack width={'100%'}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        spacing={'8px'}
        mb={'30px'}
      >
        <OutlinedInput
          placeholder={'患者ID'}
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name='patientno'
          value={searchs.patientno}
          onChange={(event) => handleInputSearch(event)}
        />
        <OutlinedInput
          placeholder={'患者名'}
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name='name'
          value={searchs.name}
          onChange={(event) => handleInputSearch(event)}
        />
        <OutlinedInput
          placeholder={'電話番号'}
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name='tel'
          value={searchs.tel}
          onChange={(event) => handleInputSearch(event)}
        />
        <OutlinedInput
          placeholder={'メールアドレス'}
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name='email'
          value={searchs.email}
          onChange={(event) => handleInputSearch(event)}
        />
        <Autocomplete
          multiple
          id='checkboxes-surgery'
          options={surgeries}
          disableCloseOnSelect
          getOptionLabel={(option: any) => option.surgery}
          renderOption={(props: any, option, { selected }) => {
            const { id, ...optionProps } = props;
            return (
              <li {...optionProps} key={id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.surgery}
              </li>
            );
          }}
          onChange={(event, value) =>
            handleMultiChange(event, value, 'surgeryids')
          }
          sx={inputMultiStyle}
          renderInput={(params) => (
            <TextField {...params} label='' placeholder='施術名' />
          )}
        />
        <OutlinedInput
          placeholder={'見積番号'}
          sx={inputStyle}
          inputProps={{
            sx: {
              backgroundColor: 'primary.light',
            },
          }}
          name='quote_id'
          value={searchs.quote_id}
          onChange={(event) => handleInputSearch(event)}
        />
        <Autocomplete
          multiple
          id='checkboxes-tags'
          options={tags}
          disableCloseOnSelect
          getOptionLabel={(option: any) => option.description}
          renderOption={(props: any, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.description}
              </li>
            );
          }}
          onChange={(event, value) =>
            handleMultiChange(event, value, 'tag_ids')
          }
          sx={inputMultiStyle}
          renderInput={(params) => (
            <TextField {...params} label='' placeholder='タグ名' />
          )}
        />
        <BtnGradient text='検索' handleButtonClick={handleSearch} />
        <BtnOutlineGradient
          text='新規作成'
          handleButtonClick={handleCreateNew}
          width='120px'
          srcStartIcon='/images/icons/ic-plus-gradient.svg'
        />
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
        }}
      >
        <Table
          sx={{
            minWidth: 650,
            '& .MuiTableCell-root.MuiTableCell-head': {
              borderBottom: '1px solid #F3F3F3',
              fontWeight: 700,
              p: '6px 16px',
            },
            '& .MuiTableCell-root.MuiTableCell-body': {
              color: 'primary.dark',
              py: '6px',
            },
          }}
          aria-label='simple table'
        >
          <TableHead>
            <TableRow>
              <TableCell>患者ID</TableCell>
              <TableCell>患者名</TableCell>
              <TableCell>電話番号</TableCell>
              <TableCell>生年月日</TableCell>
              <TableCell>年齢</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>直近の来院日</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checkAPI ? (
              <>
                {patients.map((row: any, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '& .MuiTableCell-root.MuiTableCell-body': {
                        py: '4px',
                        borderBottom: '1px solid #F3F3F3',
                      },
                    }}
                  >
                    <TableCell>{row.patientno}</TableCell>
                    <TableCell>
                      <Typography fontSize={'10px'}>{row.kana}</Typography>
                      <Typography>{row.fullname}</Typography>
                    </TableCell>
                    <TableCell>{row.phone1}</TableCell>
                    <TableCell>{row.dob}</TableCell>
                    <TableCell>{row.age}</TableCell>
                    <TableCell>
                      {row.email.split(',').map((em: string, index: number) => {
                        return <Typography key={index}>{em}</Typography>;
                      })}
                    </TableCell>
                    <TableCell>{row.inputtime}</TableCell>
                    <TableCell>
                      <BtnOutlineGradient
                        text='患者詳細へ'
                        handleButtonClick={() =>
                          handleDetailClick(row.id, row.privacy)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {patients.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      sx={{ textAlign: 'center', verticalAlign: 'center' }}
                    >
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : (
              <>
                {Array.from(Array(10)).map((_, index) => {
                  return (
                    <TableRow key={index}>
                      {Array.from(Array(8)).map((_, index) => {
                        return (
                          <TableCell key={index}>
                            <Skeleton
                              variant='rounded'
                              width={'100%'}
                              height={30}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {patients.length > 0 && pagination && (
        <Box display={'flex'} justifyContent={'right'} mt={'10px'}>
          <Typography
            component='label'
            sx={{
              color: '#AAB2BD',
              fontWeight: '400',
              fontSize: '10px',
              lineHeight: '12px',
            }}
          >
            {getDetailPagin(pagination)}
          </Typography>
        </Box>
      )}
      {patients.length > 0 && pagination && pagination.page && (
        <Pagin handlePageChange={handlePageChange} pagination={pagination} />
      )}
    </Stack>
  );
};

export default PatientList;
